import "../../../../App.css";
import Helper from "../../../../Common/Helper";
import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IdentityProvider, Role } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { Constants } from "../../../../Common/Constants";
import { Guid } from "@iconics/core";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TextField } from "@iconics/components";

export class ListRoles extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get localStorage() { return appContext().localStorageProvider; }
    private canCreate = false;
    private identityProvidersLoaded = false;
    private debounceTimer = 0;

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            identityProviders: [],
            searchTerm: '',
            gridData: { rows: [] },
            gridRefreshToken: Guid.new().toString(),
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = appContext().labels.Platform_Role_PageTitle;
        this.canCreate = this.localStorage.hasRight("DATAMODEL.Roles.Create");

        await this.getIdentityProviders()
    }

    private async getIdentityProviders(): Promise<void>
    {
        await this.loading(async () =>
        {
            let identityProviders: IdentityProvider[] = [];
            try
            {
                const identityProvidersData = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.get<PagedResponse<IdentityProvider[]>>({
                    select: IdentityProvider,
                    tenancy: appContext().tenantId,
                });
                identityProviders = identityProvidersData.value;
            } 
            catch (error)
            {
                console.error('Unable to load Identity Providers:' + error);
            }
            this.identityProvidersLoaded = true;
            this.setState({ 
                identityProviders: identityProviders, 
                gridRefreshToken: Guid.new().toString() 
            });
        });
    }

    private handleSearchTermChanged(value: string): void 
    {
        this.setState({ searchTerm: value });
        window.clearTimeout(this.debounceTimer);

        this.debounceTimer = window.setTimeout(
            () => this.setState({ gridRefreshToken: Guid.new().toString() }),
            Constants.longDebounceTimeInMilliseconds,
        );
    }

    private async handleRowsRequest(request: IGridRowsRequest<IRole>): Promise<IGridRowsResponse<IRole>>
    {
        if (!this.identityProvidersLoaded)
        {
            return { rows: [], nextSkipToken: '' }
        }

        const { searchTerm } = this.state;

        const rolesData = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.get<PagedResponse<Role[]>>({
            select: Role,
            tenancy: appContext().tenantId,
            skipToken: request.skipToken,
            top: request.take,
            filter: `contains(Name, '${searchTerm}')`,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const roles = rolesData.value.map(role => ({
            id: role.Id,
            name: role.Name,
            identityProviderId: role.IdentityProviderId,
            identityProviderName: (this.state.identityProviders.find(identityProvider => identityProvider.Id === role.IdentityProviderId)?.Name ?? role.IdentityProviderId),
            isOwner: role.IsOwner,
        } as IRole));

        return { rows: roles, nextSkipToken: rolesData.skipToken };
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            icon={<SerendieSymbol name='apps-circle' />}
                            title={this.labels.Platform_Role_ListTitle}
                            subTitle={this.labels.Platform_Role_ListSubTitle}
                        >
                            <GridContainer>
                                <GridItem expanded={3}>
                                    <TextField
                                        leftContent={<SerendieSymbol name={"magnifying-glass"}/>}
                                        placeholder={this.labels.Platform_CommonAction_Search}
                                        value={this.state.searchTerm}
                                        onChange={(event) => this.handleSearchTermChanged(event.target.value)}
                                    />
                                </GridItem>
                                <GridItem expanded={9}>
                                    <ButtonBar>
                                        <RightButtons>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='plus' />}
                                                onClick={() => this.props.navigate(`/security-roles/rolesdetails/0`)}
                                                disabled={!this.canCreate}
                                            >
                                                {this.labels.Platform_CommonAction_Add}
                                            </Button>
                                        </RightButtons>
                                    </ButtonBar>
                                </GridItem>
                                <GridItem>
                                    <DataGrid
                                        columns={[
                                            {
                                                columnId: Helper.nameOf<Role>("Name"),
                                                title: this.labels.Role_Name,
                                                getCellValue: i => i.name,
                                                columnWidth: 40,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<IRole>("identityProviderName"),
                                                title: this.labels.Role_IdentityProviderId,
                                                getCellValue: i => i.identityProviderName,
                                                columnWidth: 40,
                                            },
                                            {
                                                columnId: Helper.nameOf<Role>("IsOwner"),
                                                title: this.labels.Role_IsOwner,
                                                getCellValue: (i) => <SerendieSymbol
                                                    name={i.isOwner ? 'check-circle' : 'close-circle'}
                                                    style={{ marginLeft: '32px' }}
                                                />,
                                                columnWidth: 20,
                                                sortable: true,
                                            },
                                        ]}
                                        data={this.state.gridData}
                                        rowId={i => i.id}
                                        onChange={i => this.setState({ gridData: i })}
                                        onRowsRequest={i => this.handleRowsRequest(i)}
                                        onCellClicked={i => this.props.navigate(`/security-roles/rolesdetails/${i.id}`)}
                                        refreshToken={this.state.gridRefreshToken}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
            </>
        );
    }
}

export interface IProps extends IRouterProps
{
}

export interface IState
{
    identityProviders: IdentityProvider[];
    searchTerm: string;
    gridData: IGridData<IRole>;
    gridRefreshToken: string;
}

export interface IRole
{
    id: string;
    name: string;
    identityProviderId: number;
    identityProviderName: string;
    isOwner: boolean;
}

export default withRouting(ListRoles);
